import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { MultiSelect } from '@/components/ui/multiselect';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { financialsService } from '@/services/financials-graphql-service';
import type { PendingFilters } from '@/contexts/RightPanelContext';

const STATUS_OPTIONS = [
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Completed', value: 'Completed' },
];

const PERIOD_OPTIONS = [
  { label: 'Period 1', value: '1' },
  { label: 'Period 2', value: '2' },
  { label: 'Period 3', value: '3' },
  { label: 'Period 4', value: '4' },
  { label: 'Period 5', value: '5' },
  { label: 'Period 6', value: '6' },
  { label: 'Period 7', value: '7' },
  { label: 'Period 8', value: '8' },
  { label: 'Period 9', value: '9' },
  { label: 'Period 10', value: '10' },
  { label: 'Period 11', value: '11' },
  { label: 'Period 12', value: '12' },
];

export const DepreciationFiltersTab: React.FC = () => {
  const { 
    setActiveFilterCount,
    pendingFilters,
    setPendingFilters,
    applyFilters,
    clearAllFilters,
  } = useRightPanel();

  const [yearOptions, setYearOptions] = useState<{ label: string; value: string }[]>([]);
  const [isLoadingYears, setIsLoadingYears] = useState(false);

  const fetchYearOptions = useCallback(async () => {
    setIsLoadingYears(true);
    try {
      const years = await financialsService.getSystemYears('desc');
      setYearOptions(years.map(y => ({ label: y.label, value: y.nominal.toString() })));
    } catch {
      setYearOptions([]);
    } finally {
      setIsLoadingYears(false);
    }
  }, []);

  useEffect(() => {
    fetchYearOptions();
  }, [fetchYearOptions]);

  // Local state for filter inputs
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [depreciationMin, setDepreciationMin] = useState('');
  const [depreciationMax, setDepreciationMax] = useState('');
  const [assetCountMin, setAssetCountMin] = useState('');
  const [assetCountMax, setAssetCountMax] = useState('');
  const [referenceSearch, setReferenceSearch] = useState('');
  const [postedBySearch, setPostedBySearch] = useState('');
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [preparedBySearch, setPreparedBySearch] = useState('');
  const [preparedDateRange, setPreparedDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [totalOpeningNBVMin, setTotalOpeningNBVMin] = useState('');
  const [totalOpeningNBVMax, setTotalOpeningNBVMax] = useState('');
  const [totalClosingNBVMin, setTotalClosingNBVMin] = useState('');
  const [totalClosingNBVMax, setTotalClosingNBVMax] = useState('');

  // Sync local state with pending filters when they change externally
  useEffect(() => {
    if (pendingFilters) {
      setSelectedStatuses(pendingFilters.selectedStatuses || []);
      setDateRange(pendingFilters.transactionDateRange || { from: undefined, to: undefined });
      setDepreciationMin(pendingFilters.transactionAmountMin?.toString() || '');
      setDepreciationMax(pendingFilters.transactionAmountMax?.toString() || '');
      setSelectedPeriods(pendingFilters.selectedPeriods || []);
      setSelectedYears(pendingFilters.selectedYears || []);
      setPreparedBySearch(pendingFilters.preparedBySearch || '');
      setPreparedDateRange(pendingFilters.preparedDateRange || { from: undefined, to: undefined });
      setTotalOpeningNBVMin(pendingFilters.totalOpeningNBVMin || '');
      setTotalOpeningNBVMax(pendingFilters.totalOpeningNBVMax || '');
      setTotalClosingNBVMin(pendingFilters.totalClosingNBVMin || '');
      setTotalClosingNBVMax(pendingFilters.totalClosingNBVMax || '');
    }
  }, [pendingFilters]);

  // Update pending filters whenever local state changes
  useEffect(() => {
    const filters: Partial<PendingFilters> = {
      selectedStatuses,
      transactionDateRange: dateRange,
      transactionAmountMin: depreciationMin ? parseFloat(depreciationMin) : undefined,
      transactionAmountMax: depreciationMax ? parseFloat(depreciationMax) : undefined,
      selectedPeriods,
      selectedYears,
      preparedBySearch,
      preparedDateRange,
      assetCountMin,
      assetCountMax,
      totalOpeningNBVMin,
      totalOpeningNBVMax,
      totalClosingNBVMin,
      totalClosingNBVMax,
    };
    setPendingFilters(filters as PendingFilters);
  }, [selectedStatuses, dateRange, depreciationMin, depreciationMax, assetCountMin, assetCountMax, referenceSearch, postedBySearch, selectedPeriods, selectedYears, preparedBySearch, preparedDateRange, totalOpeningNBVMin, totalOpeningNBVMax, totalClosingNBVMin, totalClosingNBVMax, setPendingFilters]);

  // Calculate active filter count
  const activeCount = useMemo(() => {
    let count = 0;
    if (selectedStatuses.length > 0) count++;
    if (dateRange.from || dateRange.to) count++;
    if (depreciationMin || depreciationMax) count++;
    if (assetCountMin || assetCountMax) count++;
    if (referenceSearch) count++;
    if (postedBySearch) count++;
    if (selectedPeriods.length > 0) count++;
    if (selectedYears.length > 0) count++;
    if (preparedBySearch) count++;
    if (preparedDateRange.from || preparedDateRange.to) count++;
    if (totalOpeningNBVMin || totalOpeningNBVMax) count++;
    if (totalClosingNBVMin || totalClosingNBVMax) count++;
    return count;
  }, [selectedStatuses, dateRange, depreciationMin, depreciationMax, assetCountMin, assetCountMax, referenceSearch, postedBySearch, selectedPeriods, selectedYears, preparedBySearch, preparedDateRange, totalOpeningNBVMin, totalOpeningNBVMax, totalClosingNBVMin, totalClosingNBVMax]);

  useEffect(() => {
    setActiveFilterCount(activeCount);
  }, [activeCount, setActiveFilterCount]);

  const handleClearAll = () => {
    setSelectedStatuses([]);
    setDateRange({ from: undefined, to: undefined });
    setDepreciationMin('');
    setDepreciationMax('');
    setAssetCountMin('');
    setAssetCountMax('');
    setReferenceSearch('');
    setPostedBySearch('');
    setSelectedPeriods([]);
    setSelectedYears([]);
    setPreparedBySearch('');
    setPreparedDateRange({ from: undefined, to: undefined });
    setTotalOpeningNBVMin('');
    setTotalOpeningNBVMax('');
    setTotalClosingNBVMin('');
    setTotalClosingNBVMax('');
    clearAllFilters();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable Filters */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* Status */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Status</Label>
            <MultiSelect
              options={STATUS_OPTIONS}
              value={selectedStatuses}
              onValueChange={setSelectedStatuses}
              placeholder="All statuses"
            />
          </div>

          <Separator />

          {/* Date Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Date Range</Label>
            <DateRangePicker
              from={dateRange.from}
              to={dateRange.to}
              onFromChange={(date) => setDateRange(prev => ({ ...prev, from: date }))}
              onToChange={(date) => setDateRange(prev => ({ ...prev, to: date }))}
            />
          </div>

          <Separator />

          {/* Total Depreciation Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Total Depreciation Range</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={depreciationMin}
                  onChange={(e) => setDepreciationMin(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={depreciationMax}
                  onChange={(e) => setDepreciationMax(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Asset Count Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Asset Count Range</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={assetCountMin}
                  onChange={(e) => setAssetCountMin(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={assetCountMax}
                  onChange={(e) => setAssetCountMax(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Reference Search */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Reference</Label>
            <Input
              placeholder="Search by reference..."
              value={referenceSearch}
              onChange={(e) => setReferenceSearch(e.target.value)}
              className="h-9"
            />
          </div>

          <Separator />

          {/* Posted By Search */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Posted By</Label>
            <Input
              placeholder="Search by user..."
              value={postedBySearch}
              onChange={(e) => setPostedBySearch(e.target.value)}
              className="h-9"
            />
          </div>

          <Separator />

          {/* Period Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Period</Label>
            <MultiSelect
              options={PERIOD_OPTIONS}
              value={selectedPeriods}
              onValueChange={setSelectedPeriods}
              placeholder="All periods"
            />
          </div>

          <Separator />

          {/* Year Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Year</Label>
            <MultiSelect
              options={yearOptions}
              value={selectedYears}
              onValueChange={setSelectedYears}
              placeholder={isLoadingYears ? "Loading years..." : "All years"}
            />
          </div>

          <Separator />

          {/* Prepared By Search */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Prepared By</Label>
            <Input
              placeholder="Search by user..."
              value={preparedBySearch}
              onChange={(e) => setPreparedBySearch(e.target.value)}
              className="h-9"
            />
          </div>

          <Separator />

          {/* Prepared Date Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Prepared Date Range</Label>
            <DateRangePicker
              from={preparedDateRange.from}
              to={preparedDateRange.to}
              onFromChange={(date) => setPreparedDateRange(prev => ({ ...prev, from: date }))}
              onToChange={(date) => setPreparedDateRange(prev => ({ ...prev, to: date }))}
            />
          </div>

          <Separator />

          {/* Total Opening NBV Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Total Opening NBV Range</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={totalOpeningNBVMin}
                  onChange={(e) => setTotalOpeningNBVMin(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={totalOpeningNBVMax}
                  onChange={(e) => setTotalOpeningNBVMax(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Total Closing NBV Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Total Closing NBV Range</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={totalClosingNBVMin}
                  onChange={(e) => setTotalClosingNBVMin(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={totalClosingNBVMax}
                  onChange={(e) => setTotalClosingNBVMax(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex-shrink-0 border-t bg-muted/20 p-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClearAll}
            className="flex-1"
            disabled={activeCount === 0}
          >
            Clear All
          </Button>
          <Button
            onClick={applyFilters}
            className="flex-1"
            disabled={activeCount === 0}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};
